import { useEffect } from "react";
import Footer from "@/components/sections/Footer";

// Privacy Policy — canonical hosted page for FolioStax. This is the URL entered in the Google Play
// Console (App content → Privacy policy): https://fyzeron.com/foliostax/privacy-policy
// Keep the version / last-updated in sync when the copy changes; mirror any material change in the
// app's onboarding consent copy.
//
// NOTE: This is product-drafted copy, not legal advice. Have it reviewed by counsel before launch.
const POLICY_VERSION = "1.0";
const POLICY_LAST_UPDATED = "23 September 2026";

const SECTIONS = [
  {
    title: "1. Introduction",
    body:
      "This Privacy Policy explains how Fyzeron Technologies LLP (\"we\", \"us\") collects, uses, and " +
      "protects information when you use the FolioStax application (the \"App\"). By creating an " +
      "account or using the App, you agree to the practices described here. FolioStax is a business " +
      "accounting and inventory tool; the business records you enter belong to you.",
  },
  {
    title: "2. Information We Collect",
    body:
      "Account information: your name and mobile number, and (for staff accounts) the role assigned " +
      "by your organisation's admin. Business data you enter: items, inventory, customers and " +
      "suppliers, invoices and transactions, reports, print formats, and settings. Device and usage " +
      "information: a device identifier used to secure your session, app version, and diagnostic logs. " +
      "We do not ask for or store bank card numbers, passwords for other services, or government IDs.",
  },
  {
    title: "3. How We Use Information",
    body:
      "We use your information to provide and operate the App, authenticate you, sync and back up your " +
      "data across your devices, provide customer support, enforce subscription entitlements, prevent " +
      "abuse, and comply with legal obligations. We do not sell your personal information, and we do " +
      "not use your business data for advertising.",
  },
  {
    title: "4. Cloud Sync and Storage",
    body:
      "If you enable cloud features, your business data is transmitted over encrypted connections and " +
      "stored on our servers so it can sync between your devices and be restored from backup. You " +
      "remain responsible for keeping your own copies of important records. Data is logically " +
      "separated per organisation.",
  },
  {
    title: "5. Service Providers",
    body:
      "We share limited information with the following trusted providers only as needed to run the " +
      "App, each processing data on our behalf under its own security and privacy obligations:\n\n" +
      "• Twilio Inc. — sends the one-time passcodes (OTP) used to verify your mobile number and sign " +
      "you in.\n" +
      "• Google LLC (Firebase Authentication and Firebase Cloud Messaging) — helps authenticate your " +
      "sign-in and delivers push notifications to your device.\n" +
      "• Google LLC (Google Play Billing / Google Play Developer API) — processes and verifies your " +
      "subscription purchases.\n" +
      "• Amazon Web Services, Inc. (AWS) — provides the cloud servers and storage (Amazon S3) that " +
      "host and back up your data.\n" +
      "• OpenAI, L.L.C. — transcribes and interprets audio, but only when you use the optional voice " +
      "features (see section 6).\n\n" +
      "We do not authorise these providers to use your information for their own purposes.",
  },
  {
    title: "6. Voice Features",
    body:
      "Voice item-entry and dictation are optional. When you use them, the audio you record is sent to " +
      "OpenAI, L.L.C. for transcription and interpretation so the App can turn your speech into text " +
      "and item details. The audio is used only to fulfil that request and is not used to build a " +
      "profile of you or to train models. If you do not use these features, no audio is collected.",
  },
  {
    title: "7. International Data Transfers",
    body:
      "Our providers may process your information in countries other than your own — for example, our " +
      "cloud servers and backups are hosted in the European Union (AWS, Sweden), while Twilio, Google " +
      "and OpenAI may process data in the United States and elsewhere. Where required, we rely on " +
      "appropriate safeguards for such transfers.",
  },
  {
    title: "8. Data Retention",
    body:
      "We keep your information for as long as your account is active. When you delete your account, we " +
      "delete your account and associated data — except records we are legally required to retain " +
      "(such as transaction and tax records), which we keep only for the period required by applicable " +
      "law and then delete. Anonymous, aggregated statistics that cannot identify you may be retained.",
  },
  {
    title: "9. Deleting Your Account and Data",
    body:
      "You can delete your account and its data at any time from within the App (Settings → My Account " +
      "→ Delete account), or by submitting a request at " +
      "https://fyzeron.com/foliostax/delete-account. See that page for exactly what is deleted and " +
      "what may be retained for legal reasons.",
  },
  {
    title: "10. Security",
    body:
      "We apply reasonable technical and organisational safeguards — including encrypted connections " +
      "and access controls — to protect your information. No method of transmission or storage is " +
      "completely secure, so we cannot guarantee absolute security. Keep your device and login " +
      "credentials protected.",
  },
  {
    title: "11. Children",
    body:
      "FolioStax is a business tool intended for use by adults. It is not directed to children, and we " +
      "do not knowingly collect personal information from children.",
  },
  {
    title: "12. Your Rights",
    body:
      "Depending on where you live, you may have rights to access, correct, export, or delete your " +
      "personal information, and to object to or restrict certain processing. You can exercise these " +
      "rights in the App or by contacting us at the address below.",
  },
  {
    title: "13. Changes to This Policy",
    body:
      "We may update this Privacy Policy from time to time. The \"Last updated\" date reflects the " +
      "latest revision. Continued use of the App after changes take effect constitutes acceptance of " +
      "the revised policy.",
  },
  {
    title: "14. Contact Us",
    body: "For any privacy questions or requests, contact us at info@fyzeron.com.",
  },
];

export default function PrivacyPolicy() {
  useEffect(() => {
    const prev = document.title;
    document.title = "FolioStax — Privacy Policy";
    window.scrollTo(0, 0);
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center" aria-label="Fyzeron Technologies LLP — home">
            <img src="/fyzeron-logo-dark.png" alt="Fyzeron Technologies LLP" className="h-9 w-auto" />
          </a>
          <a href="/" className="text-sm font-semibold text-slate-600 hover:text-navy transition-colors">
            ← Back to home
          </a>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-800">Legal</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-navy mt-3">
            Privacy Policy
          </h1>
          <p className="mt-3 text-sm text-slate-500">
            FolioStax &middot; Version {POLICY_VERSION} &middot; Last updated {POLICY_LAST_UPDATED}
          </p>

          <div className="mt-10 space-y-8">
            {SECTIONS.map((s) => (
              <section key={s.title}>
                <h2 className="font-display font-bold text-xl text-slate-900">{s.title}</h2>
                <p className="mt-2 text-slate-600 leading-relaxed whitespace-pre-line">
                  {renderBody(s.body)}
                </p>
              </section>
            ))}
          </div>

          <p className="mt-14 pt-8 border-t border-slate-200 text-sm text-slate-400">
            © {new Date().getFullYear()} Fyzeron Technologies LLP. All rights reserved.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}

// Linkifies the deletion URL and the contact email inside a section body.
function renderBody(body) {
  const DELETE_URL = "https://fyzeron.com/foliostax/delete-account";
  const EMAIL = "info@fyzeron.com";

  if (body.includes(DELETE_URL)) {
    const [before, after] = body.split(DELETE_URL);
    return (
      <>
        {before}
        <a href="/foliostax/delete-account" className="text-blue-800 font-medium hover:underline">
          {DELETE_URL}
        </a>
        {after}
      </>
    );
  }
  if (body.includes(EMAIL)) {
    const [before, after] = body.split(EMAIL);
    return (
      <>
        {before}
        <a href={`mailto:${EMAIL}`} className="text-blue-800 font-medium hover:underline">
          {EMAIL}
        </a>
        {after}
      </>
    );
  }
  return body;
}
